import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { createIndividualForm, clssInfo } from '../../Api'
import {
    Container,
    ContainerDivs,
    Header,
    HeaderInfo,
    Title,
    Subtitle,
    StudentCard,
    Avatar,
    StudentInfo,
    StudentName,
    PeriodTag,
    EditorCard,
    EditorLabel,
    CharCount,
    StyledQuillContainer,
    Chips,
    Chip,
    ChipsHint,
    ActionBar,
    Button,
    GhostButton,
    Input,
    ErrorMessage,
    SkeletonCard,
    SkeletonLine,
    ToGoBack,
    SignMessageButtonText,
    SignMessageButtonTextBold
} from './style'

// ── helpers fora do componente ─────────────────────────────────────
const safeParse = (raw) => {
    try { return raw ? JSON.parse(raw) : null } catch { return null }
}

const extractId = (v) => {
    if (v && typeof v === 'object') return String(v._id ?? v.id ?? '')
    return v != null ? String(v) : ''
}

// mapeia o rótulo do período para o campo de quarter no model
const BIMESTRE_FIELD = {
    '1º BIMESTRE': 'id_iStQuarter',
    '2º BIMESTRE': 'id_iiNdQuarter',
    '3º BIMESTRE': 'id_iiiRdQuarter',
    '4º BIMESTRE': 'id_ivThQuarter',
    '5º BIMESTRE': 'id_vThQuarter',
    '6º BIMESTRE': 'id_viThQuarter'
}

// iniciais para o avatar
const initials = (name) => {
    if (!name) return '?'
    const parts = String(name).trim().split(/\s+/)
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?'
}

// conta palavras a partir do HTML do editor
const wordCount = (html) => {
    const text = String(html || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim()
    if (!text) return 0
    return text.split(/\s+/).length
}

// frases de apoio para agilizar a escrita
const SUGESTOES = [
    'De acordo com as observações diárias, ',
    'O aluno demonstra ',
    'Apresenta bom desenvolvimento em ',
    'Sugere-se estimular ',
    'No próximo período, é importante '
]

// importar o módulo da toolbar uma única vez (fora do componente,
// para não recriar o objeto a cada render)
const QUILL_MODULES = {
    toolbar: [
        [{ font: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['bold', 'italic', 'underline'],
        [{ color: [] }, { background: [] }],
        ['clean']
    ]
}

const Form = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const mountedRef = useRef(true)

    // funcionário (professor) escolhido pelo supervisor na tela anterior
    const { employee } = location.state || {}

    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [description, setDescription] = useState('')
    const [errorMessage, setErrorMessage] = useState('')

    // contexto
    const [selectbimonthly, setSelectbimonthly] = useState(null)
    const [bimonthly, setBimonthly] = useState('')
    const [id_student, setId_student] = useState('')
    const [nameStudent, setNameStudent] = useState('')
    const [year, setYear] = useState('')
    const [id_teacher, setId_teacher] = useState('')
    const [id_class, setId_class] = useState('')
    const [assessmentRegime, setAssessmentRegime] = useState('')

    const [regentId, setRegentId] = useState('')
    const [regent02Id, setRegent02Id] = useState('')
    const [physicalId, setPhysicalId] = useState('')

    useEffect(() => {
        mountedRef.current = true
        return () => { mountedRef.current = false }
    }, [])

    useEffect(() => {
        (async () => {
            setLoading(true)
            try {
                // supervisor: o funcionário vem do state da navegação (não do localStorage)
                const id_employee = employee
                const idClass = sessionStorage.getItem('class-info')
                const student = safeParse(sessionStorage.getItem('stdt'))
                const selBi = safeParse(sessionStorage.getItem('Selectbimonthly'))
                const nmStudent = sessionStorage.getItem('nmstdt')

                // dados de contexto (sem depender do servidor)
                setNameStudent(nmStudent ? (safeParse(nmStudent) ?? nmStudent) : '')
                setSelectbimonthly(selBi)
                setBimonthly(selBi?.bimonthly || '')
                setId_student(student?._id || '')
                setId_teacher(extractId(id_employee))
                setId_class(idClass || '')
                setAssessmentRegime(sessionStorage.getItem('assessmentRegime') || '')
                setRegentId(extractId(safeParse(sessionStorage.getItem('classRegentTeacher'))))
                setRegent02Id(extractId(safeParse(sessionStorage.getItem('classRegentTeacher02'))))
                setPhysicalId(extractId(safeParse(sessionStorage.getItem('physicalEducationTeacher'))))

                // year: tenta sessionStorage primeiro (evita ida ao servidor).
                // só chama clssInfo se não houver yearGrade salvo.
                const cachedYear = sessionStorage.getItem('yearGrade')
                if (cachedYear) {
                    setYear(cachedYear)
                } else {
                    const resClass = await clssInfo(idClass)
                    const y = resClass?.data?.data?.find(c => c.year)?.year || ''
                    if (mountedRef.current) {
                        setYear(y)
                        if (y) sessionStorage.setItem('yearGrade', y)
                    }
                }
            } catch (err) {
                console.error('Erro ao carregar contexto:', err)
            } finally {
                if (mountedRef.current) setLoading(false)
            }
        })()
    }, [employee])

    const words = useMemo(() => wordCount(description), [description])
    const isEmpty = words === 0

    const periodoLabel = useMemo(() => {
        if (!bimonthly) return ''
        return assessmentRegime === 'TRIMESTRAL'
            ? bimonthly.replace(/Bimestre/gi, 'Trimestre')
            : bimonthly.replace(/Trimestre/gi, 'Bimestre')
    }, [bimonthly, assessmentRegime])

    // insere uma sugestão no fim do texto atual
    const addSuggestion = useCallback((frase) => {
        setErrorMessage('')
        setDescription(prev => {
            const base = prev && prev !== '<p><br></p>' ? prev : ''
            return `${base}<p>${frase}</p>`
        })
    }, [])

    // cria o relatório — preserva integralmente a lógica original (regente 01/02/ed.física)
    const handleClick = useCallback(async () => {
        if (submitting) return
        if (!selectbimonthly) {
            setErrorMessage('Período não identificado. Volte e selecione novamente.')
            return
        }
        if (isEmpty) {
            setErrorMessage('Escreva o relatório antes de cadastrar.')
            return
        }

        const quarterIdKey = BIMESTRE_FIELD[selectbimonthly.bimonthly]
        if (!quarterIdKey) {
            setErrorMessage('Período inválido. Verifique os dados.')
            return
        }

        setErrorMessage('')
        setSubmitting(true)
        try {
            const idQuarter = selectbimonthly._id
            let res

            if (regentId === id_teacher) {
                res = await createIndividualForm({
                    year, id_class, description, id_student,
                    id_teacher: regentId, id_teacher02: regent02Id,
                    [quarterIdKey]: idQuarter
                })
            } else if (regent02Id === id_teacher) {
                res = await createIndividualForm({
                    year, id_class, description, id_student,
                    id_teacher: regentId, id_teacher02: regent02Id,
                    [quarterIdKey]: idQuarter
                })
            } else if (physicalId === id_teacher) {
                res = await createIndividualForm({
                    year, id_class, description, id_student,
                    id_teacher, id_teacher02: null,
                    [quarterIdKey]: idQuarter
                })
            } else {
                // fallback: professor não identificado entre os papéis
                setErrorMessage('Este professor não está vinculado a esta turma.')
                setSubmitting(false)
                return
            }

            if (res) {
                navigate(-1)
            } else {
                setErrorMessage('Não foi possível cadastrar. Tente novamente.')
                setSubmitting(false)
            }
        } catch (error) {
            console.error('Erro ao cadastrar relatório:', error)
            if (mountedRef.current) {
                setErrorMessage('Erro, verifique os dados e tente novamente.')
                setSubmitting(false)
            }
        }
    }, [submitting, selectbimonthly, isEmpty, regentId, regent02Id, physicalId,
        id_teacher, year, id_class, description, id_student, navigate])

    const goBack = useCallback(() => navigate(-1), [navigate])

    // ── skeleton ────────────────────────────────────────────────────
    if (loading) {
        return (
            <Container>
                <ContainerDivs>
                    <Header>
                        <HeaderInfo><Title>Relatório Individual</Title></HeaderInfo>
                    </Header>
                    <SkeletonCard>
                        <SkeletonLine $w="50%" $h="18px" />
                        <SkeletonLine $w="30%" />
                    </SkeletonCard>
                    <SkeletonCard>
                        <SkeletonLine $w="100%" $h="220px" />
                    </SkeletonCard>
                </ContainerDivs>
            </Container>
        )
    }

    return (
        <Container>
            <ContainerDivs>
                <Header>
                    <HeaderInfo>
                        <Title>Novo Relatório Individual</Title>
                        {periodoLabel && (
                            <Subtitle>
                                {assessmentRegime === 'TRIMESTRAL'
                                    ? periodoLabel.replace('BIMESTRE', 'TRIMESTRE')
                                    : periodoLabel}
                            </Subtitle>
                        )}
                    </HeaderInfo>
                </Header>

                {/* cartão do aluno */}
                <StudentCard>
                    <Avatar>{initials(nameStudent)}</Avatar>
                    <StudentInfo>
                        <StudentName>{nameStudent || 'Aluno'}</StudentName>
                        {periodoLabel && (
                            <PeriodTag>
                                {assessmentRegime === 'TRIMESTRAL'
                                    ? periodoLabel.replace('BIMESTRE', 'TRIMESTRE')
                                    : periodoLabel}
                            </PeriodTag>
                        )}
                    </StudentInfo>
                </StudentCard>

                {/* editor */}
                <EditorCard>
                    <EditorLabel>
                        <span>Relatório</span>
                        <CharCount $empty={isEmpty}>
                            {isEmpty ? 'Vazio' : `${words} ${words === 1 ? 'palavra' : 'palavras'}`}
                        </CharCount>
                    </EditorLabel>

                    <StyledQuillContainer>
                        <ReactQuill
                            theme="snow"
                            modules={QUILL_MODULES}
                            value={description}
                            onChange={(content) => { setDescription(content); if (errorMessage) setErrorMessage('') }}
                            placeholder="Descreva o desenvolvimento do aluno neste período..."
                        />
                    </StyledQuillContainer>

                    {/* sugestões rápidas */}
                    <ChipsHint>Sugestões para começar (toque para inserir):</ChipsHint>
                    <Chips>
                        {SUGESTOES.map((frase, i) => (
                            <Chip key={i} type="button" onClick={() => addSuggestion(frase)}>
                                {frase.trim().replace(/,$/, '')}
                            </Chip>
                        ))}
                    </Chips>
                </EditorCard>

                <Input>
                    {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
                </Input>

                <ActionBar>
                    <GhostButton onClick={goBack} disabled={submitting}>
                        Cancelar
                    </GhostButton>
                    <Button onClick={handleClick} disabled={submitting || isEmpty}>
                        {submitting ? 'Cadastrando...' : 'Cadastrar Relatório'}
                    </Button>
                </ActionBar>

                <ToGoBack onClick={goBack} role="button" tabIndex={0}>
                    <SignMessageButtonText>Voltar para a</SignMessageButtonText>
                    <SignMessageButtonTextBold>lista de Alunos</SignMessageButtonTextBold>
                </ToGoBack>
            </ContainerDivs>
        </Container>
    )
}

export default Form